/**
 * @fileoverview Convidar por e-mail, em Usuários (etapa 5 dos planos): o
 * e-mail, o perfil e a licença; a pessoa recebe o link para criar a senha e
 * entrar na conta ativa (vale 7 dias, uma vez). As regras são as do cadastro
 * de usuário: administrador, ou licença acima da própria, só com
 * team.manage_access; sem vagas de Chat Interno, as licenças com ele travam.
 * O servidor confere tudo de novo (e o e-mail confirmado de quem convida).
 */

import React, { useContext, useEffect, useState } from "react";
import { Dialog, IconButton } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import MenuItem from "@material-ui/core/MenuItem";
import { X } from "lucide-react";

import ModernSelect from "../ModernSelect";
import { isInternalLocked } from "../UserEditorModal";
import { useAuthFormStyles } from "../AuthLayout/formStyles";
import { EMAIL_RE, errorText } from "../AuthLayout/authHelpers";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useBilling } from "../../context/Billing/BillingContext";
import usePermission, { isAdminUser } from "../../hooks/usePermission";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { announcePlanLimit } from "../../utils/planLimitEvent";
import useInviteStyles from "./styles";

const t = (key, options) => i18n.t(`users.invites.${key}`, options);
const LICENSES = [
  { value: "full", label: "userModal.form.accessScopeFull", internal: true },
  { value: "attendance", label: "userModal.form.accessScopeAttendance", internal: false },
  { value: "internal", label: "userModal.form.accessScopeInternal", internal: true },
];

// O limite dos planos novos abre o aviso que oferece os planos (no lugar da mensagem). true: abriu.
export const announceInviteLimit = (err) => {
  const data = err?.response?.data || {};
  return typeof data.error === "string" && data.error.startsWith("ERR_PLAN_LIMIT_") && announcePlanLimit({ code: data.error, used: data.used, limit: data.limit });
};

const InviteDialog = ({ open, onClose, onSent }) => {
  const form = useAuthFormStyles();
  const classes = useInviteStyles();
  const { user } = useContext(AuthContext);
  const { can } = usePermission();
  const canManageAccess = can("team.manage_access", isAdminUser(user));
  const { billing } = useBilling();
  const internalLocked = isInternalLocked(billing);
  const ownLicense = user?.accessScope || "full";
  const licenseAllowed = (license) => (canManageAccess || ownLicense === "full" || license.value === ownLicense) && !(license.internal && internalLocked);
  const firstLicense = () => (LICENSES.find((license) => license.value === "attendance" && licenseAllowed(license)) || LICENSES.find(licenseAllowed) || LICENSES[1]).value;
  const [email, setEmail] = useState("");
  const [profile, setProfile] = useState("user");
  const [license, setLicense] = useState("attendance");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  // Cada abertura começa limpa, no atendimento (a licença que cabe em todo plano; o Chat Interno não).
  useEffect(() => {
    if (!open) return;
    setEmail("");
    setProfile("user");
    setLicense(firstLicense());
    setError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const send = async (event) => {
    event.preventDefault();
    const address = email.trim();
    if (!EMAIL_RE.test(address)) {
      setError(t("emailInvalid"));
      return;
    }
    setSending(true);
    setError("");
    try {
      const { data } = await api.post("/invites", { email: address, profile, accessScope: license });
      onSent(data);
      onClose();
    } catch (err) {
      // O limite do plano: o convite fecha e o aviso com os planos abre no lugar (sem modal sobre modal).
      if (announceInviteLimit(err)) onClose();
      else setError(errorText(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={sending ? undefined : onClose} maxWidth={false} PaperProps={{ className: classes.dialogPaper }} aria-labelledby="invite-dialog-title">
      <form onSubmit={send} noValidate>
        <div className={classes.dialogHead}>
          <div>
            <h2 id="invite-dialog-title" className={classes.dialogTitle}>{t("title")}</h2>
            <p className={classes.dialogText}>{t("subtitle")}</p>
          </div>
          <IconButton size="small" onClick={onClose} className={classes.dialogClose} aria-label={t("cancel")} disabled={sending}>
            <X size={18} />
          </IconButton>
        </div>
        <div className={classes.dialogBody}>
          <div className={form.field}>
            <label className={form.label} htmlFor="invite-dialog-email">{t("email")}</label>
            <input
              id="invite-dialog-email"
              type="email"
              className={form.input}
              value={email}
              placeholder={t("emailPlaceholder")}
              autoComplete="off"
              autoFocus
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <span className={form.label} id="invite-dialog-profile">{t("profile")}</span>
          <ModernSelect
            className={classes.select}
            fullWidth
            value={profile}
            onChange={(event) => setProfile(event.target.value)}
            labelId="invite-dialog-profile"
          >
            <MenuItem value="user">{t("profiles.user")}</MenuItem>
            {canManageAccess && <MenuItem value="admin">{t("profiles.admin")}</MenuItem>}
          </ModernSelect>
          <span className={form.label} id="invite-dialog-license">{t("license")}</span>
          <ModernSelect
            className={classes.select}
            fullWidth
            value={license}
            onChange={(event) => setLicense(event.target.value)}
            labelId="invite-dialog-license"
          >
            {LICENSES.map((option) => (
              <MenuItem key={option.value} value={option.value} disabled={!licenseAllowed(option)}>
                {i18n.t(option.label)}
              </MenuItem>
            ))}
          </ModernSelect>
          {error && <div className={`${form.notice} ${form.noticeBad}`} role="alert">{error}</div>}
        </div>
        <div className={classes.dialogFoot}>
          <button type="button" className={`${form.secondary} ${classes.footButton}`} onClick={onClose} disabled={sending}>{t("cancel")}</button>
          <button
            type="submit"
            className={`${form.button} ${classes.footButton}`}
            disabled={sending || !email.trim() || !licenseAllowed(LICENSES.find((option) => option.value === license) || LICENSES[1])}
          >
            {sending && <CircularProgress size={16} className={form.spinner} />}
            {t("send")}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default InviteDialog;
